import { useEffect, useState } from 'react';
import { SettingGetAll, SettingSave } from 'services/settingService';
import { toast } from 'react-hot-toast';
import LoadingOverlay from "components/Static/LoadingOverlay";

interface Setting {
    id: string;
    key: string;
    value: string;
}

export default function SettingsPage() {
    const [settings, setSettings] = useState<{
        companyName: string;
        companyId?: string;
        schoolName: string;
        schoolNameId?: string;
        confirmUser: string;
        confirmUserId?: string;
        director: string;
        directorId?: string;
        examTypes: string;
        examTypesId?: string;
        examTypesArray: string[];
        chairman: string;
        chairmanId?: string;
        vicePrincipal: string;
        vicePrincipalId?: string;
        secretary: string;
        secretaryId?: string;
        teamLeader: string;
        teamLeaderId?: string;
    }>({
        companyName: '',
        schoolName: '',
        confirmUser: '',
        examTypes: '',
        director: '',
        chairman: '',
        vicePrincipal: '',
        secretary: '',
        teamLeader: '',
        examTypesArray: []
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editingCompany, setEditingCompany] = useState(false);
    const [editingExamTypes, setEditingExamTypes] = useState(false);
    const [editingDecisions, setEditingDecisions] = useState(false);
    const [newExamType, setNewExamType] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [companyNameChanged, setCompanyNameChanged] = useState(false);
    const [decisionChanged, setDecisionChanged] = useState(false);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await SettingGetAll();
            if (response.result) {
                const companySetting = response.result.find((s: Setting) => s.key === 'CompanyName');
                const schoolNameSetting = response.result.find((s: Setting) => s.key === 'SchoolName');
                const examTypesSetting = response.result.find((s: Setting) => s.key === 'ExamTypes');
                const confirmUserSetting = response.result.find((s: Setting) => s.key === 'ConfirmUser');
                const directorSetting = response.result.find((s: Setting) => s.key === 'Director');
                const chairmanSetting = response.result.find((s: Setting) => s.key === 'Chairman');
                const vicePrincipalSetting = response.result.find((s: Setting) => s.key === 'VicePrincipal');
                const secretarySetting = response.result.find((s: Setting) => s.key === 'Secretary');
                const teamLeaderSetting = response.result.find((s: Setting) => s.key === 'TeamLeader');

                const director = directorSetting?.value || '';
                const companyName = companySetting?.value || '';
                const schoolName = schoolNameSetting?.value || '';
                const confirmUser = confirmUserSetting?.value || '';
                const examTypes = examTypesSetting?.value || '';
                const chairman = chairmanSetting?.value || '';
                const vicePrincipal = vicePrincipalSetting?.value || '';
                const secretary = secretarySetting?.value || '';
                const teamLeader = teamLeaderSetting?.value || '';

                setSettings({
                    companyName,
                    companyId: companySetting?.id,
                    schoolName,
                    schoolNameId: schoolNameSetting?.id,
                    confirmUser,
                    confirmUserId: confirmUserSetting?.id,
                    director,
                    directorId: directorSetting?.id,
                    examTypes,
                    examTypesId: examTypesSetting?.id,
                    examTypesArray: examTypes.split(',').map((item: string) => item.trim()).filter((item: any) => item),
                    chairman,
                    chairmanId: chairmanSetting?.id,
                    vicePrincipal,
                    vicePrincipalId: vicePrincipalSetting?.id,
                    secretary,
                    secretaryId: secretarySetting?.id,
                    teamLeader,
                    teamLeaderId: teamLeaderSetting?.id,
                });
            }
            setLoading(false)
        } catch (error) {
            console.error('Error fetching settings:', error);
            setError('Không thể tải cài đặt. Vui lòng thử lại.');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveCompany = async () => {
        try {
            setSaving(true);
            setError(null);

            if (!settings.companyName.trim()) {
                setError('Vui lòng nhập tên chi nhánh');
                return;
            }

            await SettingSave({
                id: settings.companyId,
                key: 'CompanyName',
                value: settings.companyName.trim()
            });
            await SettingSave({
                id: settings.schoolNameId,
                key: 'SchoolName',
                value: settings.schoolName.trim()
            });
            await SettingSave({
                id: settings.confirmUserId,
                key: 'ConfirmUser',
                value: settings.confirmUser.trim()
            });
            await SettingSave({
                id: settings.directorId,
                key: 'Director',
                value: settings.director.trim()
            });

            toast.success('Cập nhật thông tin chi nhánh thành công!');
            setEditingCompany(false);
            setCompanyNameChanged(false);
            await fetchSettings(); // Refresh data to get updated IDs if it was a new creation
        } catch (error) {
            console.error('Error saving company name:', error);
            toast.error('Lỗi khi lưu thông tin chi nhánh');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveExamTypes = async () => {
        try {
            setSaving(true);
            setError(null);

            if (settings.examTypesArray.length === 0) {
                setError('Vui lòng thêm ít nhất một kỳ thi');
                return;
            }

            const examTypesValue = settings.examTypesArray.join(', ');
            await SettingSave({
                id: settings.examTypesId,
                key: 'ExamTypes',
                value: examTypesValue
            });

            toast.success('Cập nhật kỳ thi thành công!');
            setEditingExamTypes(false);
            await fetchSettings(); // Refresh data to get updated IDs if it was a new creation
        } catch (error) {
            console.error('Error saving exam types:', error);
            toast.error('Lỗi khi lưu kỳ thi');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveDecisions = async () => {
        try {
            setSaving(true);
            setError(null);

            await SettingSave({
                id: settings.chairmanId,
                key: 'Chairman',
                value: settings.chairman.trim()
            });
            await SettingSave({
                id: settings.vicePrincipalId,
                key: 'VicePrincipal',
                value: settings.vicePrincipal.trim()
            });
            await SettingSave({
                id: settings.secretaryId,
                key: 'Secretary',
                value: settings.secretary.trim()
            });
            await SettingSave({
                id: settings.teamLeaderId,
                key: 'TeamLeader',
                value: settings.teamLeader.trim()
            });

            toast.success('Cập nhật thông tin quyết định thành công!');
            setEditingDecisions(false);
            setDecisionChanged(false);
            await fetchSettings(); // Refresh data to get updated IDs if it was a new creation
        } catch (error) {
            console.error('Error saving decision settings:', error);
            toast.error('Lỗi khi lưu thông tin quyết định');
        } finally {
            setSaving(false);
        }
    };

    const handleAddExamType = () => {
        if (!newExamType.trim()) {
            setError('Vui lòng nhập tên kỳ thi');
            return;
        }

        if (settings.examTypesArray.includes(newExamType.trim())) {
            setError('Kỳ thi này đã tồn tại');
            return;
        }

        setSettings(prev => ({
            ...prev,
            examTypesArray: [...prev.examTypesArray, newExamType.trim()]
        }));
        setNewExamType('');
        setError(null);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleAddExamType();
        }
    };

    const handleRemoveExamType = (index: number) => {
        setSettings(prev => ({
            ...prev,
            examTypesArray: prev.examTypesArray.filter((_, i) => i !== index)
        }));
    };

    const handleCompanyInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: value
        }));
        setCompanyNameChanged(true);
    };

    const handleDecisionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: value
        }));
        setDecisionChanged(true);
    };

    if (loading) {
        return (
            <LoadingOverlay message="Đang tải dữ liệu..." />
        );
    }

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-2xl font-bold uppercase text-gray-800 mb-6">Cài đặt hệ thống</h1>

            {error && (
                <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                    {error}
                </div>
            )}

            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                {/* Company Information Section */}
                <div className="p-6 border-b border-gray-200">
                    <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                            <h2 className="text-lg font-bold uppercase text-gray-900 mb-1">Thông tin chi nhánh</h2>
                            <p className="text-sm text-gray-500">Cấu hình thông tin chi nhánh và người phụ trách</p>
                        </div>
                        {!editingCompany && (
                            <button
                                onClick={() => setEditingCompany(true)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                            >
                                <i className="fa fa-edit mr-2"></i>Chỉnh sửa
                            </button>
                        )}
                    </div>

                    {editingCompany ? (
                        <div className="space-y-4">
                            {/* Company Name */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Tên chi nhánh đầy đủ <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="companyName"
                                    value={settings.companyName}
                                    onChange={handleCompanyInfoChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Nhập tên chi nhánh đầy đủ"
                                />
                                <p className="text-xs text-gray-500">
                                    <i className="fa fa-info-circle mr-1"></i>
                                    Sử dụng cho báo cáo tiến độ, kế hoạch ĐT, DS phân công, Giáo án, báo cáo 1
                                </p>
                            </div>

                            {/* School Name */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Tên chi nhánh (viết tắt)
                                </label>
                                <input
                                    type="text"
                                    name="schoolName"
                                    value={settings.schoolName}
                                    onChange={handleCompanyInfoChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Nhập tên chi nhánh riêng biệt"
                                />
                                <p className="text-xs text-gray-500">
                                    <i className="fa fa-info-circle mr-1"></i>
                                    Dùng trong Sổ tay giáo viên, Sổ theo dõi, các quyết định
                                </p>
                            </div>

                            {/* Director */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Giám đốc trung tâm
                                </label>
                                <input
                                    type="text"
                                    name="director"
                                    value={settings.director}
                                    onChange={handleCompanyInfoChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Nhập tên giám đốc"
                                />
                                <p className="text-xs text-gray-500">
                                    <i className="fa fa-info-circle mr-1"></i>
                                    Được sử dụng cho các báo cáo
                                </p>
                            </div>

                            {/* Confirm User */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Người xác nhận giáo án
                                </label>
                                <input
                                    type="text"
                                    name="confirmUser"
                                    value={settings.confirmUser}
                                    onChange={handleCompanyInfoChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Nhập tên người xác nhận giáo án"
                                />
                                <p className="text-xs text-gray-500">
                                    <i className="fa fa-info-circle mr-1"></i>
                                    Người phụ trách xác nhận các báo cáo giáo án
                                </p>
                            </div>

                            <div className="flex space-x-2 pt-2">
                                <button
                                    onClick={handleSaveCompany}
                                    disabled={saving || !companyNameChanged}
                                    className={`px-4 py-2 rounded-md font-medium ${saving ? 'bg-blue-400' : (companyNameChanged ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400')} text-white`}
                                >
                                    {saving ? 'Đang lưu...' : 'Lưu thay đổi'}
                                </button>
                                <button
                                    onClick={() => {
                                        setEditingCompany(false);
                                        fetchSettings();
                                        setCompanyNameChanged(false);
                                    }}
                                    disabled={saving}
                                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 font-medium"
                                >
                                    Hủy
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Tên chi nhánh đầy đủ</p>
                                <p className="font-medium text-gray-900">{settings.companyName || 'Chưa có thông tin'}</p>
                            </div>
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Tên chi nhánh (phụ)</p>
                                <p className="font-medium text-gray-900">{settings.schoolName || 'Chưa có thông tin'}</p>
                            </div>
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Giám đốc trung tâm</p>
                                <p className="font-medium text-gray-900">{settings.director || 'Chưa có thông tin'}</p>
                            </div>
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Người xác nhận giáo án</p>
                                <p className="font-medium text-gray-900">{settings.confirmUser || 'Chưa có thông tin'}</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Rest of your existing code remains the same */}
                {/* Exam Types Section */}
                <div className="p-6 border-b border-gray-200">
                    <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                            <h2 className="text-lg font-bold uppercase text-gray-900 mb-1">Thiết lập kỳ thi</h2>
                            <p className="text-sm text-gray-500">Quản lý các loại kỳ thi trong hệ thống</p>
                        </div>
                        {!editingExamTypes && (
                            <button
                                onClick={() => setEditingExamTypes(true)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                            >
                                <i className="fa fa-edit mr-2"></i>Chỉnh sửa
                            </button>
                        )}
                    </div>

                    {editingExamTypes ? (
                        <div className="space-y-4">
                            <div className="flex space-x-2">
                                <input
                                    type="text"
                                    value={newExamType}
                                    onChange={(e) => setNewExamType(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Nhập tên kỳ thi và nhấn Enter"
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                                <button
                                    onClick={handleAddExamType}
                                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium"
                                >
                                    <i className="fa fa-plus mr-2"></i>Thêm
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-2">
                                {settings.examTypesArray.map((type, index) => (
                                    <div key={index} className="flex items-center bg-blue-100 text-blue-800 px-3 py-1.5 rounded-full text-sm">
                                        <span className="font-medium">{type}</span>
                                        <button
                                            onClick={() => handleRemoveExamType(index)}
                                            className="ml-2 text-blue-600 hover:text-blue-800 text-lg font-bold"
                                            aria-label={`Xóa ${type}`}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                            {settings.examTypesArray.length === 0 && (
                                <p className="text-sm text-gray-500 italic">Chưa có kỳ thi nào được thêm</p>
                            )}
                            <div className="flex space-x-2 pt-2">
                                <button
                                    onClick={handleSaveExamTypes}
                                    disabled={saving}
                                    className={`px-4 py-2 rounded-md font-medium ${saving ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
                                >
                                    {saving ? 'Đang lưu...' : 'Lưu thay đổi'}
                                </button>
                                <button
                                    onClick={() => {
                                        setEditingExamTypes(false);
                                        fetchSettings();
                                    }}
                                    disabled={saving}
                                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 font-medium"
                                >
                                    Hủy
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div>
                            {settings.examTypesArray.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                    {settings.examTypesArray.map((type, index) => (
                                        <span key={index} className="bg-blue-100 text-blue-800 px-3 py-1.5 rounded-full text-sm font-medium">
                                            {type}
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-gray-500">Chưa có kỳ thi nào được thiết lập</p>
                            )}
                        </div>
                    )}
                </div>

                {/* Decision Settings Section */}
                <div className="p-6 border-b border-gray-200">
                    <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                            <h2 className="text-lg font-bold uppercase text-gray-900 mb-1">Thiết lập quyết định</h2>
                            <p className="text-sm text-gray-500">Cấu hình thông tin cho các quyết định hội đồng</p>
                        </div>
                        {!editingDecisions && (
                            <button
                                onClick={() => setEditingDecisions(true)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                            >
                                <i className="fa fa-edit mr-2"></i>Chỉnh sửa
                            </button>
                        )}
                    </div>

                    {editingDecisions ? (
                        <div className="space-y-4">
                            {/* Chairman */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Chủ tịch hội đồng
                                </label>
                                <input
                                    type="text"
                                    name="chairman"
                                    value={settings.chairman}
                                    onChange={handleDecisionChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Nhập tên chủ tịch hội đồng"
                                />
                            </div>

                            {/* Vice Principal */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Phó hiệu trưởng
                                </label>
                                <input
                                    type="text"
                                    name="vicePrincipal"
                                    value={settings.vicePrincipal}
                                    onChange={handleDecisionChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Nhập tên phó hiệu trưởng"
                                />
                            </div>

                            {/* Secretary */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Thư ký
                                </label>
                                <input
                                    type="text"
                                    name="secretary"
                                    value={settings.secretary}
                                    onChange={handleDecisionChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Nhập tên thư ký"
                                />
                            </div>

                            {/* Team Leader */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Tổ trưởng
                                </label>
                                <input
                                    type="text"
                                    name="teamLeader"
                                    value={settings.teamLeader}
                                    onChange={handleDecisionChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Nhập tên tổ trưởng"
                                />
                            </div>

                            <div className="flex space-x-2 pt-2">
                                <button
                                    onClick={handleSaveDecisions}
                                    disabled={saving || !decisionChanged}
                                    className={`px-4 py-2 rounded-md font-medium ${saving ? 'bg-blue-400' : (decisionChanged ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400')} text-white`}
                                >
                                    {saving ? 'Đang lưu...' : 'Lưu thay đổi'}
                                </button>
                                <button
                                    onClick={() => {
                                        setEditingDecisions(false);
                                        fetchSettings();
                                        setDecisionChanged(false);
                                    }}
                                    disabled={saving}
                                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 font-medium"
                                >
                                    Hủy
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Chủ tịch hội đồng</p>
                                <p className="font-medium text-gray-900">{settings.chairman || 'Chưa có thông tin'}</p>
                            </div>
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Phó hiệu trưởng</p>
                                <p className="font-medium text-gray-900">{settings.vicePrincipal || 'Chưa có thông tin'}</p>
                            </div>
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Thư ký</p>
                                <p className="font-medium text-gray-900">{settings.secretary || 'Chưa có thông tin'}</p>
                            </div>
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Tổ trưởng</p>
                                <p className="font-medium text-gray-900">{settings.teamLeader || 'Chưa có thông tin'}</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* System Info Section */}
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-lg font-medium text-gray-900 mb-4">Thông tin hệ thống</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-500">Phiên bản</p>
                            <p className="font-medium">1.0.0</p>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-500">Đơn vị thực hiện</p>
                            <p className="font-medium">Phòng IT Công Ty BXTP</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}